import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import ErrorDisplay from "../../components/ErrorDisplay/ErrorDisplay";
import Loader from "../../components/Loader/Loader";
import OnboardingFlow from "./OnboardingFlow";
import PostOnboardingModal from "./PostOnboardingModal";

/**
 * OnboardingWrapper Component - Redux Version
 *
 * Smart wrapper that manages onboarding state using Redux.
 * Handles all edge cases: network errors, retries, authentication failures.
 *
 * Features:
 * - Server-side validation (API is source of truth)
 * - Automatic error handling and retry logic
 * - Network recovery detection
 * - Comprehensive error states
 * - Analytics tracking
 * - No localStorage dependencies
 */
const OnboardingWrapper = ({
  children,
  selectedOrganization,
  userRole,
  onInviteMember,
  onboardingState,
  profileState,
  subscriptionPlans,
  checkOnboardingStatus,
  completeOnboarding,
  clearOnboardingFlags,
  getOrganizationPlans,
  isInviteLoading,
  memberInvited,
  clearMemberInvited,
  updateFirstLogin,
  loginProfileCreatedReset,
}) => {
  const history = useHistory();
  const [showPostOnboardingModal, setShowPostOnboardingModal] = useState(false);
  const [hasClosedPostOnboarding, setHasClosedPostOnboarding] = useState(false);
  const [lastCompletedAt, setLastCompletedAt] = useState(null);
  // Track isFirstLogin in state to properly handle changes
  const [isFirstLogin, setIsFirstLogin] = useState(
    () => localStorage.getItem("is_first_login") === "true",
  );


  // Initialize onboarding check on mount
  useEffect(() => {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) {
      history.push("/auth/login");
      return;
    }

    // Prevent members from accessing dashboard
    const currentUserRole =
      onboardingState?.userRole || userRole || selectedOrganization?.role;
    const currentPath = window.location.pathname;
    if (currentUserRole === "member" && currentPath.includes("/dashboard")) {
      // Redirect members based on organization type
      const orgType = selectedOrganization?.type || "operation";
      const redirectPath = orgType === "service" ? "/user/tasks" : "/user/jobs";
      history.push(redirectPath);
      return;
    }

    checkOnboardingStatus();
    getOrganizationPlans();
  }, [
    onboardingState?.userRole,
    userRole,
    selectedOrganization?.role,
    history,
    checkOnboardingStatus,
    getOrganizationPlans,
  ]);

  // Sync isFirstLogin state with localStorage changes
  useEffect(() => {
    const checkFirstLogin = () => {
      const firstLoginValue = localStorage.getItem("is_first_login") === "true";
      setIsFirstLogin(firstLoginValue);
    };

    // Check immediately
    checkFirstLogin();

    // Set up interval to check for changes (in case other tabs/windows modify it)
    const interval = setInterval(checkFirstLogin, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Don't show if onboarding check is still loading
    if (onboardingState?.isLoading) {
      return;
    }

    // Read is_first_login directly from localStorage (same pattern as Dashboard.jsx)
    // This ensures we always check the latest value, especially for invitation users
    const isFirstLogin = localStorage.getItem("is_first_login") === "true";

    // Check if user has already seen the post-onboarding modal before
    const hasSeenPostOnboarding =
      localStorage.getItem("post_onboarding_shown") === "true";

    // Case 1: Onboarding just completed successfully (has completedAt timestamp)
    // Only show if it was JUST completed (completedAt is different from last seen)
    // and user hasn't seen it before
    const currentCompletedAt = onboardingState?.completedAt;
    const isNewCompletion =
      currentCompletedAt && currentCompletedAt !== lastCompletedAt;

    const onboardingJustCompleted =
      onboardingState &&
      !onboardingState.needsOnboarding &&
      !onboardingState.error &&
      !onboardingState.isSubmitting &&
      isNewCompletion &&
      !hasSeenPostOnboarding;

    // Case 2: User has is_first_login=true and onboarding is not needed
    // IMPORTANT: Show for ALL first-time users (is_first_login === true) regardless of role
    // This includes users who come via invitation link - they don't need onboarding
    // but should see the post-onboarding modal if is_first_login === true
    // Only prevent if they've closed it in this session or if onboarding is still in progress
    const shouldShowForFirstLogin =
      onboardingState &&
      isFirstLogin && // User is on first login - read directly from localStorage
      !onboardingState.needsOnboarding && // Onboarding is not needed (already completed or not required, e.g., invitation users)
      !onboardingState.error &&
      !onboardingState.isSubmitting &&
      !hasClosedPostOnboarding; // Prevent re-showing after user closes it in this session
    // Removed hasSeenPostOnboarding check - first-time users should see it even if they've seen it before
    // The hasClosedPostOnboarding check prevents showing on every render

    const shouldShowPostOnboarding =
      onboardingJustCompleted || shouldShowForFirstLogin;

    if (shouldShowPostOnboarding && !showPostOnboardingModal) {
      // Track this completion timestamp to prevent re-showing
      if (currentCompletedAt) {
        setLastCompletedAt(currentCompletedAt);
      }

      // Small delay to ensure onboarding flow modal has closed (if it was open)
      const timeoutId = setTimeout(() => {
        setShowPostOnboardingModal(true);
      }, 300);

      // Cleanup timeout if component unmounts or dependencies change
      return () => clearTimeout(timeoutId);
    } else if (!shouldShowPostOnboarding && showPostOnboardingModal) {
      // Hide post-onboarding modal if conditions no longer met
      setShowPostOnboardingModal(false);
    }

    // Update lastCompletedAt if it changed (but don't show modal if already shown)
    if (
      currentCompletedAt &&
      currentCompletedAt !== lastCompletedAt &&
      !shouldShowPostOnboarding
    ) {
      setLastCompletedAt(currentCompletedAt);
    }
  }, [
    onboardingState?.isLoading,
    onboardingState?.needsOnboarding,
    onboardingState?.error,
    onboardingState?.isSubmitting,
    onboardingState?.completedAt,
    onboardingState?.userRole,
    userRole,
    isFirstLogin,
    showPostOnboardingModal,
    hasClosedPostOnboarding,
    lastCompletedAt,
  ]);

  const handlePostOnboardingModalClose = useCallback(() => {
    setHasClosedPostOnboarding(true);
    setShowPostOnboardingModal(false);

    // Mark that user has seen the post-onboarding modal
    localStorage.setItem("post_onboarding_shown", "true");

    if (clearOnboardingFlags) {
      clearOnboardingFlags();
    }

    // Ensure first login flag is cleared if it exists
    const isFirstLogin = localStorage.getItem("is_first_login") === "true";
    if (isFirstLogin && updateFirstLogin) {
      updateFirstLogin();
    }

    // Redirect based on user role - members should not access dashboard
    const currentUserRole = onboardingState?.userRole || userRole;
    if (currentUserRole === "member") {
      // Redirect members based on organization type
      const orgType = selectedOrganization?.type || "operation";
      const redirectPath = orgType === "service" ? "/user/tasks" : "/user/jobs";
      history.push(redirectPath);
    } else {
      // Redirect owners/admins to dashboard
      history.push("/user/dashboard");
    }
  }, [
    history,
    clearOnboardingFlags,
    updateFirstLogin,
    onboardingState?.userRole,
    userRole,
    selectedOrganization?.type,
  ]);

  // Handle onboarding submission
  const handleOnboardingSubmit = useCallback(
    async formData => {
      try {
        completeOnboarding(formData);
      } catch (error) {
        throw error; // Re-throw to show error in component
      }
    },
    [completeOnboarding],
  );

  // Handle onboarding completion
  const handleOnboardingComplete = useCallback(formData => {}, []);

  // Handle manual close (if needed for testing)
  const handleManualClose = useCallback(() => {
    clearOnboardingFlags();
  }, [clearOnboardingFlags]);

  // Show loading state while checking
  if (onboardingState?.isLoading) {
    return <Loader text="Loading..." />;
  }

  // Show error state
  if (onboardingState?.error) {
    return (
      <ErrorDisplay
        error={onboardingState.error}
        errorMessage={onboardingState.errorMessage}
        onRetry={() => checkOnboardingStatus()}
        onContinue={() => checkOnboardingStatus()}
      />
    );
  }

  return (
    <>
      {/* Render children (main app) */}
      {children}
      {/* Render onboarding modal */}
      {onboardingState?.needsOnboarding && (
        <OnboardingFlow
          isOpen={onboardingState.needsOnboarding}
          pendingInvitation={onboardingState.pendingInvitation}
          pendingJoinRequest={onboardingState.pendingJoinRequest}
          onSubmit={handleOnboardingSubmit}
          onComplete={handleOnboardingComplete}
          userInfo={profileState.profileData}
          userRole={userRole}
          onInviteMember={onInviteMember}
          isInviteLoading={isInviteLoading}
          updateFirstLogin={updateFirstLogin}
          loginProfileCreatedReset={loginProfileCreatedReset}
          subscriptionPlans={subscriptionPlans}
          onboardingState={onboardingState}
          checkOnboardingStatus={checkOnboardingStatus}
        />
      )}

      {/* Render post-onboarding modal */}
      <PostOnboardingModal
        isOpen={showPostOnboardingModal}
        toggle={handlePostOnboardingModalClose}
        selectedOrganization={selectedOrganization}
        joinLink={onboardingState?.joinLink}
        userRole={onboardingState?.userRole || userRole}
        organizationId={onboardingState?.organizationId}
        onInviteMember={onInviteMember}
        isInviteLoading={isInviteLoading}
        memberInvited={memberInvited}
        clearMemberInvited={clearMemberInvited}
        updateFirstLogin={updateFirstLogin}
        userInfo={profileState.profileData}
      />
    </>
  );
};

export default OnboardingWrapper;
